/**
 * THE "PAUSED" MARK -- one glyph, every surface.
 *
 * > Pete, 2026-09-24: *"We need to have the univerasl Pause Symbol on the
 * > appointment and ont the labor panel. Two vertical lines I think it is"* ->
 * > *"Put that smbol where ever it belongs on Pause in the floor board,
 * > laborclock, dock schdule or labor panel"*
 *
 * ---- ⛔⛔ WHY THIS EXISTS AT ALL -------------------------------------------
 *
 * `WorkTruckMark` means "a worker is clocked in on this truck RIGHT NOW", and
 * every surface drew it or drew nothing. So the appointment chip went BLANK the
 * moment Pete pressed Pause -- which was not a bug in the chip: `laborActive`
 * comes from `State = 'Active'` and it had correctly become false.
 *
 *   ** ABSENCE IS NOT A STATE. IT IS THE ABSENCE OF EVERY STATE, AND A READER
 *      CANNOT TELL "NOBODY EVER STARTED" FROM "SOMEBODY STOPPED". **
 *
 * ---- ⭐ WHY IT SHARES WorkTruckMark's FOOTPRINT ---------------------------
 *
 * Same `viewBox="0 0 48 48"`, same circular outline, same wrapper contract. A
 * caller swaps one for the other and NOTHING MOVES -- the chip, the row and the
 * board card keep their layout while the meaning changes.
 *   ** A STATE CHANGE THAT ALSO CHANGES THE LAYOUT IS TWO CHANGES, AND THE EYE
 *      ATTENDS TO THE WRONG ONE. **
 *
 * ---- ⭐ WHY A CIRCLE AND NOT TWO BARE BARS --------------------------------
 *
 * ⚠️ This renders at `h-4 w-4` on a Gantt chip -- sixteen pixels. Two bare bars
 * that small read as a rendering artefact or a double quote. Inside a ring they
 * are the media pause button, which every person on the floor has pressed ten
 * thousand times.
 *   ** A UNIVERSAL SYMBOL IS ONLY UNIVERSAL IN THE FORM PEOPLE HAVE ACTUALLY
 *      SEEN IT IN. **
 * ⭐ And it is a SHAPE, not a hue: it survives the board's configurable palette
 * (§1AV.261), a sun-bleached warehouse TV and a colour-blind loader.
 *
 * ---- ⚠️ IT DOES NOT MOVE, AND THAT IS THE POINT --------------------------
 *
 * ⛔ NO ANIMATION PROP, deliberately. WorkTruckMark takes `ringClassName` and
 * `ringStyle` because motion is what it means; the whole content of this mark is
 * that the motion STOPPED. A "gentle pulse" here would say the opposite of the
 * word underneath it.
 *
 * Rendered in `currentColor`, like its sibling, so each caller sets the colour.
 */
import type { CSSProperties } from 'react';

interface Props {
    /** Class on the wrapper. The caller owns size and colour. */
    wrapClassName: string;
    /**
     * Class on the svg itself. ⚠️ Kept as a separate prop rather than folded
     * into the wrapper because the board sizes its glyphs off `--f-truck` on the
     * svg while the app sizes the wrapper in rem -- the same split WorkTruckMark
     * documents and for the same reason.
     */
    markClassName?: string;
    markStyle?: CSSProperties;
    /** Ring thickness. Small renderings need a heavier stroke to read at all. */
    ringStrokeWidth?: number;
    /** Hover text. Omit on the board, where nobody has a mouse. */
    title?: string;
}

export function PauseMark({
    wrapClassName,
    markClassName,
    markStyle,
    ringStrokeWidth = 3.5,
    title,
}: Props) {
    return (
        <span className={wrapClassName} title={title} aria-hidden="true">
            <svg className={markClassName} style={markStyle} viewBox="0 0 48 48">
                {/* ⚠️ r=19 MATCHES WorkTruckMark's ARC RADIUS EXACTLY. The two
                    glyphs sit on the same optical circle, so swapping them does
                    not nudge the baseline of the text beside them. */}
                <circle cx="24" cy="24" r="19" fill="none" stroke="currentColor"
                        strokeWidth={ringStrokeWidth} />
                {/* ⭐ THE TWO BARS. Rounded caps and a gap wider than a bar, which
                    is what keeps them legible at 16px -- a tighter pair merges
                    into one thick stroke on a low-DPI warehouse display. */}
                <rect x="17" y="15" width="5" height="18" rx="2.2" fill="currentColor" />
                <rect x="26" y="15" width="5" height="18" rx="2.2" fill="currentColor" />
            </svg>
        </span>
    );
}

/**
 * ---- ⭐⭐ THE "DONE" MARK, THE THIRD MEMBER OF THE FAMILY (2026-09-24) ------
 *
 * > Pete: *"I think the glyph on the appointment card because its small."*
 *
 * ⚠️ IT LIVES IN PauseMark.tsx BECAUSE THIS FILE IS THE **STATIC** HALF OF THE
 * WORK-STATE GLYPHS, and `WorkTruckMark.tsx` is the animated one. ⛔ The filename no
 * longer describes its contents, which is a real cost -- but renaming it churns four
 * import sites and the deploy manifest for no behaviour change, and a header that
 * says what is inside is cheaper than a rename nobody can review.
 *   ** A FILENAME THAT HAS OUTGROWN ITS CONTENTS IS A DOCUMENTATION PROBLEM UNTIL
 *      SOMEBODY LOOKS FOR THE SECOND THING AND FAILS TO FIND IT. **
 * ⬜ Renaming to `WorkStateMarks.tsx` is an open item.
 *
 * ⭐ SAME viewBox, SAME r=19. Three glyphs, one footprint: orbiting truck (somebody
 * is on it), pause bars (somebody was), tick (the work is finished). The tile's
 * layout never moves as a load walks through them.
 */
export function DoneMark({
    wrapClassName,
    markClassName,
    markStyle,
    ringStrokeWidth = 3.5,
    title,
}: Props) {
    return (
        <span className={wrapClassName} title={title} aria-hidden="true">
            <svg className={markClassName} style={markStyle} viewBox="0 0 48 48">
                <circle cx="24" cy="24" r="19" fill="none" stroke="currentColor"
                        strokeWidth={ringStrokeWidth} />
                {/* ⭐ A HEAVY, SHORT TICK. At 16px a thin one with long tails reads as a
                    smudge; this sits well inside the ring with room around it. */}
                <path d="M15 24.5 L21.5 31 L33 19" fill="none" stroke="currentColor"
                      strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        </span>
    );
}
